import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import SEO from '../SEO/SEO';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-hot-toast';
import { validateMobile } from '../../utils/helpers';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const FestivalCampaign = () => {
  // Helper to get API URL
  const getApiUrl = (endpoint) => {
    // Use environment variable if available, otherwise check for localhost
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocal ? 'http://localhost:8001' : '';
    return `${baseUrl}${endpoint}`;
  };

  const [loanType, setLoanType] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [fullName, setFullName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [message, setMessage] = useState("");
  const [banner, setBanner] = useState(null);
  const [contactSettings, setContactSettings] = useState({
    heading: 'Connect With Us',
    description: 'Our dedicated team is ready to assist you with your housing finance needs. Get in touch now.',
    address: 'G129, Ground Floor, Phase 3,\nSpencer Plaza Mall, 786-789, Anna Salai,\nChennai, Tamil Nadu 600002',
    phone: '78239 11555',
    email: 'info@ahamhfc.com'
  });
  const [source, setSource] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("Anakapally");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // OTP States
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Check if all required fields are filled
 // const isFormValid = loanType && loanAmount && fullName && city && state && phone && validateMobile(phone) && source.length > 0 && couponCode.trim() !== "";
  const isFormValid = loanType && loanAmount && fullName && city && state && phone && validateMobile(phone);

  
  // Check if submit button should be enabled
  const isSubmitEnabled = isFormValid && otpVerified;

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/admin/banners/active?page_type=festival_campaign');
        if (response.data.success && response.data.data.length > 0) {
          setBanner(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      }
    };
    const fetchSettings = async () => {
      try {
        const response = await axios.get(getApiUrl('/api/festival-campaign/settings'));
        if (response.data.success) {
          setContactSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchBanner();
    fetchSettings();
  }, []);

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!phone) {
      toast.error("Please enter your phone number first");
      return;
    }
    if (!validateMobile(phone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsSendingOtp(true);

    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success("OTP sent to your mobile number!");
        // If in dev mode, log it locally for convenience
        if (data.dev_otp) {
          console.log('Dev OTP:', data.dev_otp);
        }
      } else {
        toast.error(data.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP Send error:", error);
      toast.error("An error occurred while sending OTP. Please try again later.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    const verifyToast = toast.loading("Verifying OTP...");

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success("OTP verified successfully!", { id: verifyToast });
      } else {
        toast.error(data.message || "Invalid or expired OTP", { id: verifyToast });
      }
    } catch (error) {
      console.error("OTP Verify error:", error);
      toast.error("An error occurred during verification.", { id: verifyToast });
    }
  };

  // Reset OTP state when phone number changes
  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      toast.error("Please verify your phone number with OTP first");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Sending your inquiry...");

    try {
      const response = await fetch(getApiUrl('/api/festival-campaign/submit'), {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          loanType,
          loanAmount,
          fullName,
          city,
          state,
          phone,
          couponCode,
          message,
          source: source.length > 0 ? 'Festival Campaign - ' + source.join(', ') : 'Festival Campaign'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || "Your inquiry has been sent!", { id: loadingToast });
        navigate('/thank-you');
        // Reset form fields
        setLoanType("");
        setLoanAmount("");
        setFullName("");
        setCity("");
        setState("");
        setPhone("");
        setCouponCode("");
        setMessage("");
        setSource([]);
        setOtpSent(false);
        setOtp("");
        setOtpVerified(false);
      } else {
        toast.error(data.message || "Failed to send inquiry. Please try again.", { id: loadingToast });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred while sending your inquiry. Please try again later.", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const branches = [
    {
      name: "Anakapally",
      region: "AP",
      inception: "25-Mar-25",
      address: "10-06-68/1, II Floor, Pillavaari Veedhi, Anakapalli - 531001",
      contact: "9176032225",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=83.0000%2C17.6800%2C83.0100%2C17.6900&amp;layer=mapnik&amp;marker=17.6850%2C83.0050",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=17.6850&mlon=83.0050#map=15/17.6850/83.0050",
    },
    {
      name: "Guntur",
      region: "AP",
      inception: "11-Feb-22",
      address: "6-4-9/1, Arundelpet, Guntur - 522001",
      contact: "7824852579",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=80.4300%2C16.3000%2C80.4400%2C16.3100&amp;layer=mapnik&amp;marker=16.3050%2C80.4350",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=16.3050&mlon=80.4350#map=15/16.3050/80.4350",
    },
    {
      name: "Nellore",
      region: "AP",
      inception: "20-Mar-23",
      address: "3rd floor, Sridhar's Krishna Towers, Annamayya circle, Mini By Pass Road, Nellore - 524003",
      contact: "7824045935",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=79.9700%2C14.4300%2C79.9800%2C14.4400&amp;layer=mapnik&amp;marker=14.4350%2C79.9750",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=14.4350&mlon=79.9750#map=15/14.4350/79.9750",
    },
    {
      name: "Rajamundry",
      region: "AP",
      inception: "26-Feb-25",
      address: "DNO:79-02-04/1B, Tilak Road Revenue Ward No:30, Rajahmundry, Andhra Pradesh - 533103",
      contact: "9989796410",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=81.7600%2C16.9900%2C81.7700%2C17.0000&amp;layer=mapnik&amp;marker=16.9950%2C81.7650",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=16.9950&mlon=81.7650#map=15/16.9950/81.7650",
    },
  ];

  // Schema for SEO
  const contactSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/festival-campaign/#webpage",
        "url": "https://ahamhfc.com/festival-campaign",
        "name": "Festival Campaign - Aham Housing Finance",
        "description": "Join our Festival Campaign and get exclusive offers on home loans from Aham Housing Finance.",
      }
    ]
  };

  return (
    <div className="bg-gray-50 font-sans text-gray-900 overflow-hidden">

      <SEO
        title="Festival Campaign - Aham Housing Finance"
        description="Join our Festival Campaign and get exclusive offers on home loans from Aham Housing Finance. Apply today!"
        keywords="Aham Housing Finance festival campaign, festival home loan offers, Aham festival offers"
        canonical="https://ahamhfc.com/festival-campaign"
        ogTitle="Festival Campaign - Aham Housing Finance"
        ogDescription="Join our Festival Campaign and get exclusive offers on home loans from Aham Housing Finance. Apply today!"
        schema={contactSchema}
      />

      {/* Hero Section */}
      <section
        className="relative min-h-[70vh] md:min-h-[85vh] flex items-center justify-start overflow-hidden bg-cover bg-[position:75%_top] md:bg-top"
        style={{
          backgroundImage: `url('${banner?.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : `/${banner.image_url}`) : '/img/Home_banner/Slide-03.webp'}')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/70 via-primary-800/50 to-primary-700/30"></div>
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.3, ease: "easeOut" }}
          className="relative z-10 text-left px-6 md:px-16 lg:px-24 w-full"
        >
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight drop-shadow-lg"
          >
            {banner?.title || "Festival Campaign"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium max-w-lg"
          >
            {banner?.subtitle || "Let us help you find the perfect home financing solution this festival season."}
          </motion.p>

          {/* Breadcrumb Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center justify-start space-x-2 mt-6"
          >
            <Link
              to="/"
              className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 text-white/60" />
            <span className="text-yellow-300 text-sm font-semibold">Festival Campaign</span>
          </motion.div>

        </motion.div>
      </section>

      {/* Main Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 relative z-20 mb-16">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1.2 }}
             className="bg-white rounded-2xl p-5 pr-20 md:p-10 shadow-xl space-y-8 order-2 md:order-1"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 text-center md:text-left">{contactSettings.heading}</h2>
            <p className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">
              {contactSettings.description}
            </p>
            <div className="grid gap-6">
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <MapPinIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Address</h3>
                  <p className="text-gray-600 text-sm md:text-base whitespace-pre-line">
                    {contactSettings.address}
                  </p>
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <PhoneIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Phone</h3>
                  <p className="text-gray-600 text-sm md:text-base">{contactSettings.phone}</p>
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <EnvelopeIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Email</h3>
                  <p className="text-gray-600 text-sm md:text-base">{contactSettings.email}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1.2 }}
             className="bg-white rounded-2xl p-5 pr-20 md:p-10 shadow-xl space-y-6 order-1 md:order-2"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 text-center md:text-left">Start Your Loan Journey</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Fill out the form below to get started with your loan application.
            </p>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="loanType" className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Type*
                </label>
                <select
                  id="loanType"
                  value={loanType}
                  onChange={(e) => setLoanType(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                >
                  <option value="">Select Loan Type</option>
                  <option value="homeConstruction">Home Construction Loan</option>
                  <option value="purchaseResale">Home Purchase or Resale Loan</option>
                  <option value="compositeLoan">Composite Loan</option>
                  <option value="homeRenovation">Home Renovation Loan</option>
                  <option value="homeExtension">Home Extension Loan</option>
                  <option value="balanceTransfer">Balance Transfer Loan</option>
                  <option value="nriHome">NRI Home Loan</option>
                  <option value="topUpLoan">Top-up Loan</option>
                  <option value="mortgage">Mortgage Loan</option>
                </select>
              </div>
              <div>
                <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Amount (INR)*
                </label>
                <input
                  type="number"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="Enter loan amount"
                  className="w-full p-3 md:p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div>
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name*
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    City*
                  </label>
                  <input
                    type="text"
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter your city"
                    className="w-full p-3 md:p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                    State*
                  </label>
                  <select
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full p-3 md:p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition bg-white"
                    required
                  >
                    <option value="">Select State</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Telangana">Telangana</option>
                  </select>
                </div>
              </div>
              {/* Phone Number with OTP */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number*
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    className={`flex-1 p-4 border rounded-xl focus:ring-2 focus:ring-blue-600 transition ${otpVerified ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      }`}
                    required
                    disabled={otpVerified}
                  />
                  {!otpVerified && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp || !phone || phone.length < 10}
                      className={`px-3 md:px-4 py-2 rounded-xl font-medium transition-all text-sm md:text-base ${isSendingOtp || !phone || phone.length < 10
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      {isSendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Send OTP'}
                    </button>
                  )}
                  {otpVerified && (
                    <div className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-xl font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Verified
                    </div>
                  )}
                </div>
              </div>

              {/* OTP Input - Only show if OTP sent and not yet verified */}
              {otpSent && !otpVerified && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-blue-50 p-4 rounded-xl border border-blue-200"
                >
                  <label htmlFor="otp" className="block text-sm font-medium text-blue-800 mb-2">
                    Enter OTP sent to your mobile
                  </label>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="w-full md:flex-1 p-4 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-600 transition text-center text-lg tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otp.length < 6}
                      className={`w-full md:w-auto px-6 py-3 md:py-2 rounded-xl font-medium transition-all ${otp.length < 6
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                      Verify
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Please enter the 6-digit code sent to your mobile number.
                  </p>
                </motion.div>
              )}
              {/* Coupon Code Field */}
              {/* <div>
                <label htmlFor="couponCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code*
                </label>
                <input
                  type="text"
                  id="couponCode"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter your coupon code"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div> */}

              {/* Source Checkboxes */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source*
                </label>
                <div className="flex flex-wrap gap-4">
                  {["Facebook", "Instagram", "Website", "Google", "YouTube"].map((src) => (
                    <label key={src} className="flex items-center space-x-2 cursor-pointer bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-100 transition">
                      <input
                        type="checkbox"
                        value={src}
                        checked={source.includes(src)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSource([...source, src]);
                          } else {
                            setSource(source.filter((s) => s !== src));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{src}</span>
                    </label>
                  ))}
                </div>
              </div> */}

              {/* Additional Details */}
              {/* <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Provide any additional information (e.g., purpose of loan)"
                  rows="4"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                ></textarea>
              </div> */}

              <motion.button
                whileHover={isSubmitEnabled && !isSubmitting ? { scale: 1.05 } : {}}
                whileTap={isSubmitEnabled && !isSubmitting ? { scale: 0.98 } : {}}
                disabled={!isSubmitEnabled || isSubmitting}
                className={`w-full py-3 rounded-xl font-semibold shadow-md transition-all ${isSubmitEnabled && !isSubmitting
                  ? 'bg-blue-900 text-white hover:bg-blue-800 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : !otpVerified ? 'Verify Phone to Submit' : 'Submit Application'}
              </motion.button>
              {!isSubmitEnabled && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  {!isFormValid
                    ? 'Please fill all required fields'
                    : 'Please verify your phone number with OTP to submit'}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default FestivalCampaign;
// EOF
